<template>
  <v-dialog
    :value="value"
    fullscreen
    persistent
    @input="$emit('input', $event)"
  >
    <v-card
      tile
      class="d-flex flex-column rsv-wizard-card"
    >
      <v-card-title class="text-h5 font-weight-bold">
        卓を作成
        <span class="wizard-muted ms-3">
          予約: {{ reservationSummary }}
        </span>
        <v-spacer />
        <v-btn
          icon
          @click="close"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <wizard-stepper
        class="rsv-wizard-stepper"
        :current-step="step"
        :step-names="['ホール', '卓', '確認・修正']"
        :next-disabled="!canGoNext"
        @navigate="goToStep"
      />

      <v-card-text class="rsv-wizard-body flex-grow-1 pt-4 px-6">
        <div class="rsv-wizard-content">
          <!-- Step1: ホール -->
          <div v-if="step === 1">
            <div class="wz-grid wz-grid--halls">
              <button
                v-for="hall in hallOptions"
                :key="`hall-${hall.id}`"
                type="button"
                class="wz-cell"
                :class="{ 'wz-cell--sel': hallId === hall.id }"
                @click="selectHall(hall.id)"
              >
                {{ hall.name }}
              </button>
            </div>
          </div>

          <!-- Step2: 卓（稼働中は選択不可、卓割当済みの予約がある卓は注記） -->
          <div v-else-if="step === 2">
            <div class="wz-grid">
              <button
                v-for="cell in tableCells"
                :key="`table-${cell.number}`"
                type="button"
                class="wz-cell"
                :class="{ 'wz-cell--sel': tableNumber === cell.number, 'wz-cell--busy': cell.busy }"
                :disabled="cell.busy"
                @click="selectTable(cell.number)"
              >
                {{ cell.label }}
                <small v-if="cell.busy">稼働中</small>
                <small
                  v-else-if="cell.note"
                  class="wz-cell__note"
                >{{ cell.note }}</small>
              </button>
            </div>
            <div class="wizard-muted text-caption mt-2">
              稼働中（未会計）の卓は選択できません。卓割当済みの予約がある卓は注記付きです
            </div>
          </div>

          <!-- Step3: 確認・修正（予約時の入力をここで修正してから作成できる） -->
          <div v-else>
            <v-row dense>
              <v-col cols="6">
                <div class="drum-label">
                  開始時間（初期値: 現在時刻）
                </div>
                <time-option-drum-picker
                  v-model="form.startTime"
                  class="drum-box"
                  :options="startTimeOptions"
                />
              </v-col>
              <v-col
                cols="6"
                class="d-flex flex-column"
              >
                <!-- 左カラムのラベルと上端を揃えるスペーサー -->
                <div class="drum-label">
&nbsp;
                </div>
                <div class="wz-fields-fill">
                  <v-text-field
                    v-model.number="form.customerCount"
                    label="人数"
                    type="number"
                    min="1"
                    outlined
                    hide-details
                  />
                  <div>
                    <v-select
                      v-model="form.courseId"
                      :items="courseOptions"
                      label="コース（必須）"
                      :error="courseMissing"
                      outlined
                      hide-details
                      clearable
                    />
                    <div
                      v-if="courseMissing"
                      class="wz-course-hint mt-2"
                    >
                      <v-icon
                        size="16"
                        color="error"
                      >
                        {{ icons.mdiAlertCircleOutline }}
                      </v-icon>
                      卓の作成にはコースが必要です（終了予定・セット計上に使われます）
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <div class="wz-sec-label">
              指名<template v-if="form.nominations.length > 0">
                （{{ form.nominations.length }}件 設定済み）
              </template>
            </div>
            <div
              v-if="form.nominations.length > 0"
              class="d-flex flex-wrap align-center wz-chips mb-2"
            >
              <v-chip
                v-for="(row, i) in form.nominations"
                :key="`nom-${row.userId}-${row.nominationId}`"
                color="primary"
                close
                @click:close="form.nominations.splice(i, 1)"
              >
                <b class="me-1">{{ castNameOf(row.userId) }}</b>・{{ nominationNameOf(row.nominationId) }}
              </v-chip>
              <v-btn
                v-if="!showNominationInput"
                small
                text
                color="primary"
                @click="showNominationInput = true"
              >
                ＋ 指名を追加
              </v-btn>
            </div>
            <!-- 追加入力は指名ゼロのときだけ常時表示（設定済みチップより目立って「未入力」に見えないように） -->
            <v-row
              v-if="form.nominations.length === 0 || showNominationInput"
              dense
            >
              <v-col cols="5">
                <v-select
                  v-model="nominationDraft.userId"
                  :items="castOptions"
                  label="キャスト"
                  outlined
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="5">
                <v-select
                  v-model="nominationDraft.nominationId"
                  :items="nominationOptions"
                  label="指名種別"
                  outlined
                  hide-details
                  clearable
                />
              </v-col>
              <v-col
                cols="2"
                class="d-flex align-center"
              >
                <v-btn
                  small
                  outlined
                  block
                  :disabled="!nominationDraft.userId || !nominationDraft.nominationId"
                  @click="addNomination"
                >
                  追加
                </v-btn>
              </v-col>
            </v-row>
            <div class="wizard-muted text-caption mt-3">
              予約時の入力をここで修正してから卓を作成できます（開始時間のデフォルトは現在時刻）
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="rsv-wizard-footer px-6 py-3">
        <v-btn
          text
          :disabled="step === 1 || isSaving"
          @click="step -= 1"
        >
          戻る
        </v-btn>
        <span class="wizard-muted ms-2">{{ selectionSummary }}</span>
        <v-spacer />
        <v-btn
          v-if="step < 3"
          color="primary"
          x-large
          :disabled="!canGoNext"
          @click="step += 1"
        >
          次へ
        </v-btn>
        <v-btn
          v-else
          color="primary"
          x-large
          :disabled="courseMissing"
          :loading="isSaving"
          @click="submit"
        >
          この内容で卓を作成
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mdiClose, mdiAlertCircleOutline } from '@mdi/js'
import ReservationApi from '@/api/waiter/Reservation'
import { reservationTime } from '@/views/waiter/reservations/reservationDisplayUtils'
import { commitNominationDraft } from '@/views/waiter/reservations/reservationPayload'
import { buildTimeOptions, currentTimeRounded } from '@/utils/timeOptions'
import TimeOptionDrumPicker from '@/views/components/time/TimeOptionDrumPicker.vue'
import WizardStepper from '@/views/components/table/TableCreateWizard/WizardStepper.vue'

// 予約からの卓作成ウィザード（ホール → 卓 → 確認・修正）。
// halls は HallApi.getHallOverview() の正規化行
// （{id, name, tableCount, tablePrefix, tableSuffix, busyTableNumbers}）を渡す
export default {
  name: 'ReservationCheckInWizard',
  components: { TimeOptionDrumPicker, WizardStepper },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // 対象予約（serializerの生データ行）
    reservation: {
      type: Object,
      default: null,
    },
    halls: {
      type: Array,
      default: () => [],
    },
    casts: {
      type: Array,
      default: () => [],
    },
    courses: {
      type: Array,
      default: () => [],
    },
    // 指名種別（Nomination vendibles の生データ）
    nominations: {
      type: Array,
      default: () => [],
    },
    // 同日の予約一覧（卓割当済み予約の注記に使用）
    dayReservations: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      icons: { mdiClose, mdiAlertCircleOutline },
      step: 1,
      hallId: null,
      tableNumber: null,
      form: {
        startTime: '',
        customerCount: 1,
        courseId: null,
        nominations: [],
      },
      nominationDraft: { userId: null, nominationId: null },
      isSaving: false,
      // 手順3の保存（updateReservation）成功後の最新行。卓作成だけ失敗した場合の
      // 再試行で、自分の更新に起因する409にならないようこちらを優先して参照する
      savedReservation: null,
      // ウィザードを開いた時点の現在時刻（開始時間のデフォルト・選択肢の先頭）
      currentTimeValue: '',
      // 指名の追加入力行の表示（設定済みがある場合は「＋指名を追加」で開く）
      showNominationInput: false,
    }
  },
  computed: {
    reservationSummary() {
      const attrs = this.reservationAttrs()
      const time = reservationTime(attrs.startTime) || '未定'

      return `${time}〜 ${attrs.customerCount || 1}名（内容は手順3で修正できます）`
    },
    hallOptions() {
      return this.halls.map(h => ({ id: h.id, name: h.name }))
    },
    selectedHallData() {
      if (!this.hallId) return null

      return this.halls.find(h => h.id === this.hallId)
    },
    // 選択ホールの稼働中（未会計）卓番号
    busyTableNumbers() {
      return new Set(this.selectedHallData?.busyTableNumbers || [])
    },
    // 選択ホール内で他予約（確定/来店中）に卓割当済みの番号 → 「予約 HH:mm」注記
    reservedTableNotes() {
      const notes = new Map()
      this.dayReservations.forEach(r => {
        if (r.id === this.reservation?.id) return
        const a = r.attributes || {}
        if (!['confirmed', 'checked_in'].includes(a.status)) return
        if (Number(a.hall?.id) !== this.hallId || !a.tableNumber) return
        if (!notes.has(Number(a.tableNumber))) {
          notes.set(Number(a.tableNumber), `予約${reservationTime(a.startTime) || ''}`)
        }
      })

      return notes
    },
    tableCells() {
      const hall = this.selectedHallData
      if (!hall) return []
      const prefix = hall.tablePrefix || ''
      const suffix = hall.tableSuffix || ''

      return Array.from({ length: hall.tableCount || 0 }, (_, i) => ({
        number: i + 1,
        label: `${prefix}${i + 1}${suffix}`,
        busy: this.busyTableNumbers.has(i + 1),
        note: this.reservedTableNotes.get(i + 1) || null,
      }))
    },
    // 予約登録フォームと同じ30分刻みの選択肢。先頭に現在時刻（分単位）を足して
    // 「今すぐ卓を開ける」既定値を選べるようにする
    // 5分刻み（初期値の現在時刻も5分に丸めるため選択肢に必ず含まれる）
    startTimeOptions() {
      return buildTimeOptions()
    },
    canGoNext() {
      if (this.step === 1) return !!this.hallId
      if (this.step === 2) return !!this.tableNumber

      return true
    },
    selectionSummary() {
      const parts = []
      const hall = this.hallOptions.find(h => h.id === this.hallId)
      if (hall) parts.push(hall.name)
      const cell = this.tableCells.find(c => c.number === this.tableNumber)
      if (cell) parts.push(cell.label)
      if (this.step === 3 && this.form.startTime) parts.push(this.form.startTime)

      return parts.join(' / ')
    },
    // 卓作成にはコースが無条件で必須（通常の卓作成ウィザード・BE側の検証と同じ規約）
    courseMissing() {
      return !this.form.courseId
    },
    castOptions() {
      return this.casts.map(c => ({
        text: c.attributes?.name || c.name,
        value: parseInt(c.id, 10),
      }))
    },
    nominationOptions() {
      return this.nominations.map(n => ({
        text: n.attributes?.name || n.name,
        value: parseInt(n.id, 10),
      }))
    },
    courseOptions() {
      return this.courses.map(c => ({
        text: c.attributes?.name || c.name,
        value: parseInt(c.id, 10),
      }))
    },
  },
  watch: {
    value(v) {
      if (v) this.reset()
    },
    // 開いた直後にpropの反映が遅れた場合や、開いている間に予約が再取得された場合も
    // 手順3のフォーム（人数・コース・指名）へ予約データを反映し直す
    reservation() {
      if (this.value) this.initFormFromReservation()
    },
  },
  methods: {
    reset() {
      this.step = 1
      this.hallId = this.hallOptions.length === 1 ? this.hallOptions[0].id : null
      this.tableNumber = null
      // 現在時刻が選択肢(16:00〜翌05:55)に無い時間帯は先頭にフォールバック
      // （選択肢外の値だとドラムの選択帯が空表示になるため）
      const rounded = currentTimeRounded()
      this.currentTimeValue = this.startTimeOptions.includes(rounded) ? rounded : this.startTimeOptions[0]
      this.savedReservation = null
      this.initFormFromReservation()
    },
    // computedのキャッシュだと「閉じたダイアログ内でpropが別tickで更新された」場合に
    // 古い値を掴むことがある（フロア/ボードで予約が空のままウィザードが開くバグの原因）
    // ため、予約のattributesは都度メソッドで直接読む
    reservationAttrs() {
      return (this.savedReservation || this.reservation)?.attributes || {}
    },
    initFormFromReservation() {
      const attrs = this.reservationAttrs()
      this.form = {
        startTime: this.currentTimeValue,
        customerCount: attrs.customerCount || 1,
        courseId: attrs.course?.id || null,
        nominations: (attrs.nominations || []).map(n => ({ userId: n.userId, nominationId: n.nominationId })),
      }
      this.nominationDraft = { userId: null, nominationId: null }
      this.showNominationInput = false
    },
    // ステッパーからのジャンプ。戻りは自由、進みは1stepずつ検証しながら
    goToStep(target) {
      if (target < this.step) {
        this.step = target

        return
      }
      while (this.step < target && this.canGoNext) this.step += 1
    },
    selectHall(id) {
      if (this.hallId !== id) this.tableNumber = null
      this.hallId = id
    },
    selectTable(number) {
      this.tableNumber = number
    },
    addNomination() {
      const { userId, nominationId } = this.nominationDraft
      if (!userId || !nominationId) return
      const exists = this.form.nominations.find(n => n.userId === userId && n.nominationId === nominationId)
      if (exists) {
        this.$emit('error', '同じ指名が既に追加されています')

        return
      }
      this.form.nominations.push({ userId, nominationId })
      this.nominationDraft = { userId: null, nominationId: null }
      this.showNominationInput = false
    },
    castNameOf(userId) {
      const cast = this.casts.find(c => parseInt(c.id, 10) === Number(userId))

      return cast?.attributes?.name || cast?.name || `ID:${userId}`
    },
    nominationNameOf(nominationId) {
      const nomination = this.nominations.find(n => parseInt(n.id, 10) === Number(nominationId))

      return nomination?.attributes?.name || nomination?.name || `ID:${nominationId}`
    },
    // 開始時刻をdatetime文字列に。正午前（深夜〜午前）は営業日の翌暦日として扱う
    // （16〜29時制。朝6時台の「現在時刻」でも別営業日にならないように。サーバ側でも営業日検証あり）
    buildStartAt() {
      const date = String(this.reservationAttrs().reservedDate || '').slice(0, 10)
      if (!date || !this.form.startTime) return null
      const hour = Number(this.form.startTime.slice(0, 2))
      if (hour >= 12) return `${date} ${this.form.startTime}`

      const d = new Date(`${date}T00:00:00`)
      d.setDate(d.getDate() + 1)
      const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

      return `${next} ${this.form.startTime}`
    },
    async submit() {
      const committed = commitNominationDraft(this.nominationDraft, this.form.nominations)
      if (!committed.ok) {
        this.$emit('error', committed.error)

        return
      }
      this.form.nominations = committed.nominations
      this.nominationDraft = { userId: null, nominationId: null }
      if (this.courseMissing) {
        this.$emit('error', '卓の作成にはコースが必要です。コースを選択してください')

        return
      }

      this.isSaving = true
      try {
        // 手順3の修正内容を予約へ反映してから卓を作成する
        const { data } = await ReservationApi.updateReservation(this.reservation.id, {
          customerCount: this.form.customerCount || 1,
          courseId: this.form.courseId,
          nominations: this.form.nominations.map(n => ({ userId: n.userId, nominationId: n.nominationId })),
          ...(this.reservationAttrs().updatedAt ? { expectedUpdatedAt: this.reservationAttrs().updatedAt } : {}),
        })
        if (data?.reservation?.data) this.savedReservation = data.reservation.data
        await ReservationApi.checkInReservation(this.reservation.id, {
          hallId: this.hallId,
          tableNumber: this.tableNumber,
          startAt: this.buildStartAt(),
        })
        this.$emit('completed')
        this.close()
      } catch (e) {
        const msg = e.response?.data?.errors?.join(', ') || '卓の作成に失敗しました'
        this.$emit('error', msg)
      } finally {
        this.isSaving = false
      }
    },
    close() {
      this.$emit('input', false)
    },
  },
}
</script>

<style scoped>
/* 全画面ウィザード: 卓作成ウィザード(TableCreateWizard)と同じく
   カードをviewport高さに固定し、本文だけスクロール・フッターは画面下部に固定 */
.rsv-wizard-card {
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  overflow: hidden;
}

.rsv-wizard-body {
  overflow-y: auto;
}

/* タブレット全画面で間延びしないよう本文は中央寄せで幅を絞る */
.rsv-wizard-content {
  max-width: 1080px;
  margin: 0 auto;
  width: 100%;
  /* タブレットで文字が細く見えるため本文の基本ウェイトを上げる */
  font-weight: 500;
}

.rsv-wizard-footer {
  flex: 0 0 auto;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
}

.wizard-muted {
  font-size: 16px;
  color: #94a3b8;
  font-weight: 500;
}

/* ステッパー(卓作成ウィザードと同じ番号付きヘッダー) */
.rsv-wizard-stepper {
  flex: 0 0 auto;
}

.rsv-wizard-stepper ::v-deep .v-stepper__header {
  box-shadow: none;
  border-bottom: 1px solid rgba(148, 163, 184, 0.25);
  max-width: 1080px;
  margin: 0 auto;
}

.rsv-wizard-stepper ::v-deep .v-stepper__label {
  font-size: 18px;
  font-weight: 700;
}

.rsv-wizard-stepper ::v-deep .v-stepper__step__step {
  width: 36px;
  height: 36px;
  min-width: 36px;
  font-size: 17px;
}

.rsv-wizard-stepper ::v-deep .v-stepper__step {
  padding: 20px 24px;
}

/* ホール・卓の選択グリッド */
.wz-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
}

.wz-grid--halls {
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
}

.wz-cell {
  border: 1px solid rgba(148, 163, 184, 0.55);
  border-radius: 8px;
  padding: 24px 10px;
  text-align: center;
  font-weight: 700;
  font-size: 19px;
  font-family: inherit;
  color: inherit;
  background: transparent;
  cursor: pointer;
}

.wz-cell:hover:not(:disabled) {
  border-color: #9155fd;
}

.wz-cell--sel {
  background: #9155fd;
  border-color: #9155fd;
  color: #fff;
}

.wz-cell--busy {
  background: rgba(148, 163, 184, 0.12);
  color: #94a3b8;
  font-weight: 500;
  cursor: not-allowed;
  border-style: dashed;
}

.wz-cell small {
  display: block;
  font-size: 14.5px;
  font-weight: 400;
}

.wz-cell__note {
  color: #b45309;
}

/* セクション見出し（右側に区切り線を伸ばす） */
.wz-sec-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: #94a3b8;
  margin: 18px 0 12px;
}

.wz-sec-label::after {
  content: '';
  flex: 1;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
}

/* 指名あり×コース未選択の警告 */
.wz-course-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.07);
  border: 1px solid rgba(239, 68, 68, 0.35);
  border-radius: 6px;
  padding: 6px 10px;
}

.wz-chips {
  gap: 6px;
}

/* 広い画面でドラムが間延びしないよう幅上限 */
.drum-box {
  max-width: 400px;
}

.drum-label {
  font-size: 13.5px;
  color: #94a3b8;
  font-weight: 500;
  margin-bottom: 4px;
}

/* 右カラム: ドラムと同じ高さに広げ、人数を上端・コースを下端に配置して揃える */
.wz-fields-fill {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
}

/* タブレット向け: Vuetifyのtext-caption(12px)をこの画面では読みやすいサイズに上書き */
.text-caption {
  font-size: 13px !important;
  font-weight: 500 !important;
}
</style>
